// src/components/terms/CompleteTerms.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaCheckCircle, 
  FaUserLock, 
  FaBan, 
  FaCopyright, 
  FaExclamationTriangle,
  FaTimesCircle,
  FaGavel,
  FaEnvelope,
  FaQuestionCircle,
  FaChevronDown,
  FaPlus, 
  FaMinus
} from 'react-icons/fa';

const CompleteTerms = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [activeTab, setActiveTab] = useState('terms'); // 'terms' or 'faqs'

  // Last updated date
  const lastUpdated = "March 15, 2026";

  // Terms Sections
  const termsSections = [
    {
      icon: <FaCheckCircle  />,
      title: "Acceptance of Terms",
      content: "By accessing or using our services, you automatically agree to these Terms. If you disagree with any part, please do not use our services. Continued use constitutes acceptance of any future modifications."
    },
    {
      icon: <FaUserLock  />,
      title: "Eligibility",
      content: "You must be at least 18 years old to use this service. By using our platform, you confirm you meet this age requirement and that all information you provide is accurate and complete."
    },
    {
      icon: <FaBan />,
      title: "Prohibited Activities",
      content: "You agree NOT to: (1) Violate any laws; (2) Infringe on intellectual property; (3) Distribute malware/harmful code; (4) Harass others; (5) Attempt unauthorized access; (6) Interfere with service operations."
    },
    {
      icon: <FaCopyright />,
      title: "Intellectual Property",
      content: "All content—including text, graphics, logos, software, and designs—is owned by us and protected by copyright laws. You may not copy, modify, or distribute without written permission."
    },
    {
      icon: <FaExclamationTriangle />,
      title: "Disclaimer & Liability",
      content: "Service provided 'AS IS' without warranties. We are NOT liable for any direct, indirect, or consequential damages. To the maximum extent permitted by law, our total liability shall not exceed the amount you paid us."
    },
    {
      icon: <FaTimesCircle  />,
      title: "Termination",
      content: "We reserve the right to suspend or terminate your access immediately, without notice, for violations of these Terms. You may also terminate your account at any time."
    },
    {
      icon: <FaGavel  />,
      title: "Governing Law",
      content: "These Terms are governed by the laws of [Your State/Country]. Any disputes shall be resolved exclusively in the courts of [Your City/State]. You consent to personal jurisdiction there."
    },
    {
      icon: <FaEnvelope />,
      title: "Contact Us",
      content: "For questions or concerns:",
      contact: {
        email: "info@stayinhospitality.com",
        address: "Centaurus, Islamabad, Pakistan",
        phone: "+923034935404",
        response: "We aim to respond within 2-3 business days."
      }
    }
  ];

  // FAQs Section
  const faqs = [
    {
      question: "What happens if I violate the terms?",
      answer: "Violation of terms may result in immediate account suspension or termination. In serious cases, we may pursue legal action or report to authorities. You'll receive a notification explaining the reason for any action taken."
    },
    {
      question: "Can I use your service for commercial purposes?",
      answer: "Yes, commercial use is allowed as long as it complies with our terms. However, you cannot resell our service or content without explicit written permission. Please contact us for partnership opportunities."
    },
    {
      question: "How do I cancel my account?",
      answer: "You can cancel your account anytime through your dashboard settings. Go to Profile → Settings → Account → 'Delete Account'. Your data will be permanently removed within 30 days."
    },
    {
      question: "Do you offer refunds?",
      answer: "Refund requests are handled on a case-by-case basis. Please contact our support within 14 days of purchase. Digital services may not be eligible for refunds once accessed."
    },
    {
      question: "How do you protect my data?",
      answer: "We use industry-standard encryption, secure servers, and regular security audits. Your data is never sold to third parties. For details, see our Privacy Policy."
    },
    {
      question: "Can I transfer my account to someone else?",
      answer: "Accounts are non-transferable without written consent. In case of business acquisition or merger, accounts may be transferred with notice to users."
    },
    {
      question: "What happens to my content if I delete my account?",
      answer: "Your content is permanently deleted within 30 days of account deletion. We recommend downloading any important data before initiating deletion."
    },
    {
      question: "How often do you update these terms?",
      answer: "Terms are reviewed quarterly and updated as needed. Major changes will be notified via email. The 'Last Updated' date at the top shows when changes were made."
    }
  ];

  // Toggle FAQ function
  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <section className="py-12">
      <div className="container-custom">
        
        {/* Header with Tabs */}
        <div className="text-center mb-8 space-y-2">
          <div className="inline-flex items-center gap-2">
            <span className="text-sm text-gray-600">Last Updated:</span>
            <span className="text-sm font-semibold text-[#c9a86c]">{lastUpdated}</span>
          </div>
          
          <h2 className="text-4xl !mb-6 lg:text-5xl font-light text-[#1a2b3c] leading-tight">
            Legal <span className="font-bold text-[#c9a86c]">Information</span>
          </h2>
          
          {/* Tab Buttons */}
          <div className="flex 2xl:flex-row flex-col items-center justify-center gap-4 mb-8">
            <button
              onClick={() => setActiveTab('terms')}
             className="group bg-[#c9a86c] text-white px-6 py-4 rounded-full font-medium hover:bg-[#b38f5a] transition text-md inline-flex items-center justify-center gap-2"

            >
            Terms & Conditions
            </button>
            <button
              onClick={() => setActiveTab('faqs')}
             className="bg-transparent text-black border border-black px-8 py-4 rounded-full font-medium hover:bg-[#c9a86c] hover:text-white hover:border-none transition-all duration-300 inline-flex items-center gap-2 justify-center group"
            >
              Frequently Asked Questions
            </button>
          </div>
        </div>

        {/* Terms Section */}
        {activeTab === 'terms' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {termsSections.map((section, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-4 group p-8 rounded-2xl bg-white shadow-lg hover:shadow-xl transition duration-300 border border-gray-100 hover:border-gray-100"
                >
                  
                     <div className="flex-shrink-0 w-14 h-14 bg-[#c9a86c]/10 rounded-full flex items-center justify-center group-hover:bg-[#c9a86c] transition-colors duration-300">
                    <span className="text-[#c9a86c] text-xl group-hover:text-white transition-colors duration-300">
                      {section.icon}
                    </span>
                  </div>
                  
                    <div className="flex-1">
                      <h3 className="text-md font-bold text-[#1a2b3c] mb-1 flex items-center gap-2">
                        {section.title}
                      </h3>
                      <p className="md:text-[17px] 2xl:text-lg text-gray-600 leading-relaxed mb-3">
                        {section.content}
                      </p>
                      
                      {/* Contact details if section is Contact Us */}
                      {section.contact && (
                        <div className="mt-3 space-y-1 text-sm">
                          <p className="text-[#c9a86c]">📧 {section.contact.email}</p>
                          <p className="text-gray-400">📍 {section.contact.address}</p>
                          <p className="text-gray-400">📞 {section.contact.phone}</p>
                          <p className="text-gray-500 text-xs mt-2">⏱️ {section.contact.response}</p>
                        </div>
                      )}
                    </div>
                  
                </div>
              ))}
            </div>
          </>
        )}

        {/* FAQs Section */}
       {activeTab === 'faqs' && (
  <div className="max-w-4xl mx-auto">
    <div className="text-center mb-8">
      <FaQuestionCircle className="text-5xl text-[#c9a86c] mx-auto mb-4" />
      <h3 className="text-4xl !mb-4 lg:text-5xl font-light text-[#1a2b3c] leading-tight">
        Frequently <span className="font-bold text-[#c9a86c]">Asked Questions</span>
      </h3>
      <p className="md:text-[17px] 2xl:text-lg text-gray-600">
        Quick answers to common questions about our terms and services
      </p>
    </div>

    <div className="space-y-4">
      {faqs.map((faq, index) => (
        <motion.div
          key={index}
          className="bg-white p-1 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300 border border-gray-100 overflow-hidden"
        >
          
          {/* Question */}
          <button
            onClick={() => toggleFaq(index)}
            className="w-full px-6 py-4 flex items-center justify-between text-left group"
          >
            <div className="flex items-center gap-4">
              
              {/* Icon Circle
              <div className="flex-shrink-0 w-10 h-10 bg-[#c9a86c]/10 rounded-full flex items-center justify-center group-hover:bg-[#c9a86c] transition-colors duration-300">
                <FaQuestionCircle className="text-[#c9a86c] group-hover:text-white transition-colors duration-300" />
              </div> */}

              <span className="2xl:text-lg text-[15px] text-[#1a2b3c] font-medium">
                {faq.question}
              </span>
            </div>

            <div className="w-8 h-8 rounded-full bg-[#c9a86c]/10 flex items-center justify-center">
              {openFaq === index ? (
                <FaMinus className="text-[#c9a86c] text-sm" />
              ) : (
                <FaPlus className="text-[#c9a86c] text-sm" />
              )}
            </div>
          </button>

          {/* Answer */}
          {openFaq === index && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="px-6 pb-5 pt-2 border-t border-gray-100"
            >
              <div className="flex items-start gap-4">
                
                {/* Spacer for alignment */}
                {/* <div className="w-8 h-8 flex-shrink-0"></div> */}

                <div className="flex-1">
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                    {faq.answer}
                  </p>
                </div>

              </div>
            </motion.div>
          )}
        </motion.div>
      ))}
    </div>
  </div>
)}
      </div>
    </section>
  );
};

export default CompleteTerms;